import { ProjectContext, SideBarContext } from "../../contexts";
import { ProjectSidebar, ProjectTabContentSkeleton } from "./components";
import { ProjectTab, validTabs } from "./ViewProjectPage.types";
import { ResponsiveContent, Tabs, TabsContent } from "@facade/components/ui";
import { Suspense, lazy, use, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { cn } from "@facade/lib/utils";
import { ModelsViewSkeleton } from "./views/ModelsView/ModelsView.skeleton";

const ActivityView = lazy(() =>
  import("./views/ActivityView").then((module) => ({
    default: module.ActivityView,
  })),
);

const IssuesView = lazy(() =>
  import("./views/IssuesView").then((module) => ({
    default: module.IssuesView,
  })),
);

const BoardView = lazy(() =>
  import("./views/BoardView").then((module) => ({
    default: module.BoardView,
  })),
);

const StatisticsView = lazy(() =>
  import("./views/StatisticsView").then((module) => ({
    default: module.StatisticsView,
  })),
);

const ModelsView = lazy(() =>
  import("./views/ModelsView").then((module) => ({
    default: module.ModelsView,
  })),
);

const ConfigurationsView = lazy(() =>
  import("./views/ConfigurationsView").then((module) => ({
    default: module.ConfigurationsView,
  })),
);

const PeopleView = lazy(() =>
  import("./views/PeopleView").then((module) => ({
    default: module.PeopleView,
  })),
);

const ProjectTabContent = ({ activeTab }: { activeTab: ProjectTab }) => {
  switch (activeTab) {
    case "activity":
      return <ActivityView />;

    case "issues":
      return <IssuesView />;

    case "board":
      return <BoardView />;

    case "statistics":
      return <StatisticsView />;

    case "models":
      return <ModelsView />;

    case "team":
      return <PeopleView />;

    case "settings":
      return <ConfigurationsView />;

    default:
      return null;
  }
};

const ProjectTabFallback = ({ activeTab }: { activeTab: ProjectTab }) =>
  activeTab === "models" ? (
    <ModelsViewSkeleton />
  ) : (
    <ProjectTabContentSkeleton />
  );

const ViewProjectPage = () => {
  const navigate = useNavigate();
  const { projectId, tab } = useParams();
  const { open: isSidebarOpen, toggle: toggleSidebar } = use(SideBarContext);

  if (!projectId) {
    throw new Error("Cannot render without project id");
  }

  const activeTab: ProjectTab = validTabs.includes(tab as ProjectTab)
    ? (tab as ProjectTab)
    : "issues";
  const usesFullHeightLayout = activeTab === "board" || activeTab === "models";

  useEffect(() => {
    if (!tab || !validTabs.includes(tab as ProjectTab)) {
      navigate(`/projects/${projectId}/issues`, { replace: true });
    }
  }, [projectId, tab, navigate]);

  return (
    <ProjectContext.Provider value={{ projectId }}>
      <div
        className={cn(
          "w-full flex-1 pt-4 scrollbar-gutter-stable",
          activeTab === "board"
            ? "flex min-h-0 flex-col overflow-hidden"
            : "overflow-auto",
          activeTab === "models" && "flex min-h-0 flex-col",
        )}
      >
        <Tabs
          data-open={isSidebarOpen}
          orientation="vertical"
          value={activeTab}
          className={cn(
            "flex w-full flex-1 flex-row gap-4",
            usesFullHeightLayout && "min-h-0",
            "[--project-sidebar-width:62px]",
            "data-[open=true]:[--project-sidebar-width:240px]",
          )}
          onValueChange={(value) => {
            navigate(`/projects/${projectId}/${value}`);
          }}
        >
          <ProjectSidebar
            projectId={projectId}
            open={isSidebarOpen}
            onOpenChange={toggleSidebar}
          />

          <div
            className={cn(
              "flex min-w-0 w-full flex-1 justify-center p-4 pt-0",
              usesFullHeightLayout && "min-h-0",
              "pl-[calc(var(--project-sidebar-width)+1rem)]",
              "transition-[padding-left] duration-150 ease-[cubic-bezier(0.22,1,0.36,1)]",
              "will-change-[padding-left]",
            )}
          >
            <ResponsiveContent
              className={cn(
                usesFullHeightLayout && "min-h-0",
                activeTab === "models" && "flex flex-col",
              )}
            >
              <TabsContent
                value={activeTab}
                className={cn(
                  "flex",
                  activeTab === "board"
                    ? "h-full min-h-0"
                    : activeTab === "models"
                      ? "min-h-0 flex-1"
                      : "min-h-full",
                )}
              >
                <Suspense
                  fallback={<ProjectTabFallback activeTab={activeTab} />}
                >
                  <ProjectTabContent activeTab={activeTab} />
                </Suspense>
              </TabsContent>
            </ResponsiveContent>
          </div>
        </Tabs>
      </div>
    </ProjectContext.Provider>
  );
};

export default ViewProjectPage;
